/*
 * flexa.fit free gift ladder — native replacement for the third-party gift apps.
 *
 * What it does
 *   - Watches the cart and auto-adds every £0 gift variant the basket qualifies for.
 *   - Gifts stack: a £22 basket receives the £9, £15 and £22 gifts together.
 *   - Removes a gift again if the basket drops back below its threshold.
 *   - If the shopper removes a gift themselves, it stays out until they ask for it
 *     back, or until the basket falls below the threshold and rises again.
 *   - Renders the progress UI into every [data-ff-gift-ladder] and [data-ff-gift-bar]
 *     mount on the page.
 *
 * Why it is an asset and not inline
 *   The cart drawer and cart page re-render through a section morph, and inline
 *   <script> tags arriving in morphed markup never execute. Anything inline here
 *   would silently stop working after the first cart update. This file is loaded
 *   unconditionally from layout/theme.liquid instead, and re-renders into fresh
 *   mounts on cart:updated and via a MutationObserver.
 *
 * Qualifying total
 *   Sum of final_line_price for every non-gift line, so line and cart level
 *   discounts both reduce it. Gift lines are £0 and therefore cannot change the
 *   total, which is what stops add/remove from oscillating.
 *
 * Two things worth not undoing
 *   1. Every write goes through /cart/update.js with ABSOLUTE quantities, never
 *      /cart/add.js. add.js responds with just the lines it added, so assigning
 *      its response to our cart state made the engine believe the basket was £0
 *      and strip the gifts it had only just added. update.js returns the whole
 *      cart, and absolute quantities make a reconcile idempotent, so a repeat or
 *      an overlapping run cannot double up.
 *   2. The opening cart state is seeded from Liquid in #FFGiftCart, so a page load
 *      costs no cart request at all and the first paint is already correct.
 */
(function () {
  'use strict';

  if (window.__ffGiftLadderBound) return;
  window.__ffGiftLadderBound = true;

  var cfgEl = document.getElementById('FFGiftConfig');
  if (!cfgEl) return;

  var CFG;
  try {
    CFG = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }
  if (!CFG || !Array.isArray(CFG.tiers) || !CFG.tiers.length) return;

  var TIERS = CFG.tiers
    .filter(function (t) {
      return t && t.variantId && t.threshold != null;
    })
    .sort(function (a, b) {
      return a.threshold - b.threshold;
    });
  if (!TIERS.length) return;

  var EXCLUDE_SKUS = CFG.excludeSkus || [];
  // Where the progress bar links to. Empty string in the config turns the link off
  // and the bar goes back to being purely informational.
  var PAGE_URL = typeof CFG.pageUrl === 'string' ? CFG.pageUrl : '';
  // A rung may offer several variants of one gift, for example putty in five
  // resistances. Every one of them has to count as a gift line, otherwise a
  // variant the shopper is swapping away from would be read as a paid item.
  function tierVariantIds(tier) {
    if (tier.variants && tier.variants.length) {
      return tier.variants.map(function (v) {
        return v.variantId;
      });
    }
    return [tier.variantId];
  }

  function readChoice() {
    try {
      return JSON.parse(window.localStorage.getItem(LS_CHOICE) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function writeChoice(map) {
    try {
      window.localStorage.setItem(LS_CHOICE, JSON.stringify(map));
    } catch (e) {}
  }

  // The variant this rung should put in the cart. A saved choice wins, then a
  // pot already sitting in the cart, then the config default. The middle step
  // matters: on a fresh device or cleared storage there is no saved choice, and
  // without it the reconcile would silently swap a chosen resistance back to
  // the default. The fallback also means a stale or tampered localStorage value
  // can never add nothing.
  function chosenVariantId(tier) {
    if (!tier.variants || !tier.variants.length) return tier.variantId;
    var ids = tierVariantIds(tier);
    var saved = readChoice()[tier.key];
    if (ids.indexOf(saved) !== -1) return saved;
    var line = cart ? lineFor(cart, tier) : null;
    if (line && ids.indexOf(line.variant_id) !== -1) return line.variant_id;
    return ids.indexOf(tier.variantId) !== -1 ? tier.variantId : ids[0];
  }

  var GIFT_IDS = TIERS.reduce(function (acc, t) {
    return acc.concat(tierVariantIds(t));
  }, []);
  var MAX = TIERS[TIERS.length - 1].threshold;

  var LS_DECLINED = 'ff:gift-declined';
  // Which variant the shopper picked for a multi-variant rung, keyed by tier key.
  // Survives page loads so the putty resistance they chose is still theirs when
  // they come back to finish the order.
  var LS_CHOICE = 'ff:gift-choice';
  var LS_SEEN = 'ff:gift-seen';
  var LS_COLLAPSED = 'ff:gift-bar-collapsed';
  var LS_DRAWER_OPEN = 'ff:gift-drawer-open';

  var busy = false;
  var queued = false;
  var timer = null;

  // Seeded from Liquid so the first render needs no network round trip.
  var cart = (function () {
    var el = document.getElementById('FFGiftCart');
    if (!el) return null;
    try {
      var seed = JSON.parse(el.textContent);
      return seed && Array.isArray(seed.items) ? seed : null;
    } catch (e) {
      return null;
    }
  })();

  /* ------------------------------------------------------------------ utils */

  function route(name, fallback) {
    var routes = (window.theme && window.theme.routes) || {};
    return routes[name] || fallback;
  }

  function readSet(key) {
    try {
      var raw = JSON.parse(window.localStorage.getItem(key) || '[]');
      return new Set(Array.isArray(raw) ? raw : []);
    } catch (e) {
      return new Set();
    }
  }

  function writeSet(key, set) {
    try {
      window.localStorage.setItem(key, JSON.stringify(Array.from(set)));
    } catch (e) {
      /* Safari private mode */
    }
  }

  function money(pence) {
    var pounds = pence / 100;
    return '£' + (pence % 100 === 0 ? String(pence / 100) : pounds.toFixed(2));
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ------------------------------------------------------------- cart maths */

  function isGiftLine(item) {
    return GIFT_IDS.indexOf(item.variant_id) !== -1;
  }

  function qualifyingTotal(c) {
    var total = 0;
    (c && c.items ? c.items : []).forEach(function (item) {
      if (isGiftLine(item)) return;
      if (item.sku && EXCLUDE_SKUS.indexOf(item.sku) !== -1) return;
      total += item.final_line_price;
    });
    return total;
  }

  function lineFor(c, tier) {
    var ids = tierVariantIds(tier);
    var items = (c && c.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (ids.indexOf(items[i].variant_id) !== -1) return items[i];
    }
    return null;
  }

  function quantityOf(c, variantId) {
    var items = (c && c.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === variantId) return items[i].quantity;
    }
    return 0;
  }

  function tierByKey(key) {
    for (var i = 0; i < TIERS.length; i++) {
      if (TIERS[i].key === key) return TIERS[i];
    }
    return null;
  }

  /* --------------------------------------------------------- cart requests */

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function post(url, body) {
    body.sections = bundledSections();
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-FF-Gift': '1'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json();
    });
  }

  function fetchCart() {
    return fetch(route('cart_url', '/cart') + '.js', {
      headers: { Accept: 'application/json', 'X-FF-Gift': '1' },
      cache: 'no-store'
    }).then(function (res) {
      return res.json();
    });
  }

  // Tell the theme to redraw the drawer and cart page from the section HTML the
  // cart just returned. Concept listens on its own pubsub channel; the document
  // event is the fallback for anything else bound to it.
  function announce(parsed) {
    var pubsub = window.theme && window.theme.pubsub;
    if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartUpdate) {
      pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'ff-gift-ladder',
        cart: parsed
      });
    } else {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsed } }));
    }
  }

  // Absolute quantities keyed by variant id, e.g. {123: 1, 456: 0}. Returns the
  // full cart. Deliberately not /cart/add.js — see the note at the top of the file.
  function applyQuantities(updates) {
    return post(route('cart_update_url', '/cart/update.js'), { updates: updates });
  }

  function isFullCart(value) {
    return !!value && Array.isArray(value.items) && typeof value.total_price === 'number';
  }

  /* -------------------------------------------------------------- reconcile */

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(reconcile, delay == null ? 120 : delay);
  }

  async function reconcile() {
    if (busy) {
      queued = true;
      return;
    }
    busy = true;

    try {
      if (!cart) cart = await fetchCart();

      var declined = readSet(LS_DECLINED);
      var seen = readSet(LS_SEEN);
      var total = qualifyingTotal(cart);
      var mutated = false;

      // Pass 1: work out intent before touching the cart.
      TIERS.forEach(function (tier) {
        var unlocked = total >= tier.threshold;
        var present = !!lineFor(cart, tier);

        if (!unlocked) {
          // Dropping below the threshold resets the shopper's choice, so the gift
          // is offered again next time they qualify.
          declined.delete(tier.key);
          seen.delete(tier.key);
        } else if (seen.has(tier.key) && !present) {
          // It was in the basket while qualifying and now it is not, so they took
          // it out. Respect that instead of fighting them for it.
          declined.add(tier.key);
        }
      });

      // Pass 2: work out the target quantity for every tier and send one request.
      // Gifts are £0 so none of this changes the qualifying total, which is what
      // makes a single pass sufficient.
      var updates = {};
      TIERS.forEach(function (tier) {
        var unlocked = total >= tier.threshold;
        var want = unlocked && !declined.has(tier.key) ? 1 : 0;
        var chosen = chosenVariantId(tier);
        // Absolute quantities across every variant of the rung, so switching
        // resistance removes the old pot in the same request that adds the new
        // one and a repeated run cannot double up.
        tierVariantIds(tier).forEach(function (vid) {
          var target = vid === chosen ? want : 0;
          if (target !== quantityOf(cart, vid)) updates[vid] = target;
        });
      });

      if (Object.keys(updates).length) {
        try {
          var next = await applyQuantities(updates);
          if (isFullCart(next)) {
            cart = next;
            mutated = true;
          } else {
            cart = await fetchCart();
          }
        } catch (err) {
          // Leave the cart as it was; the next event reconciles again.
        }
      }

      // Pass 3: snapshot what is genuinely in the basket now.
      total = qualifyingTotal(cart);
      TIERS.forEach(function (tier) {
        if (total < tier.threshold) {
          seen.delete(tier.key);
        } else if (lineFor(cart, tier)) {
          seen.add(tier.key);
        }
      });

      writeSet(LS_DECLINED, declined);
      writeSet(LS_SEEN, seen);

      if (mutated) announce(cart);
      render();
    } catch (err) {
      render();
    } finally {
      busy = false;
      if (queued) {
        queued = false;
        schedule(0);
      }
    }
  }

  /* ----------------------------------------------------------------- render */

  function state() {
    var total = cart ? qualifyingTotal(cart) : 0;
    var declined = readSet(LS_DECLINED);
    var rows = TIERS.map(function (tier) {
      var line = cart ? lineFor(cart, tier) : null;
      var unlocked = total >= tier.threshold;
      // A multi-variant gift shows the photo of the pot the shopper picked,
      // so the thumb agrees with the cart line and with the chips.
      var image = tier.image;
      if (tier.variants && tier.variants.length) {
        var cvid = chosenVariantId(tier);
        for (var vi = 0; vi < tier.variants.length; vi++) {
          if (tier.variants[vi].variantId === cvid && tier.variants[vi].image) {
            image = tier.variants[vi].image;
            break;
          }
        }
      }
      return {
        tier: tier,
        image: image,
        inCart: !!line,
        unlocked: unlocked,
        declined: unlocked && !line && declined.has(tier.key),
        remaining: Math.max(0, tier.threshold - total)
      };
    });
    var next = null;
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i].unlocked) {
        next = rows[i];
        break;
      }
    }
    return {
      total: total,
      rows: rows,
      next: next,
      wonCount: rows.filter(function (r) {
        return r.inCart;
      }).length,
      unlockedCount: rows.filter(function (r) {
        return r.unlocked;
      }).length
    };
  }

  function trackHtml(s) {
    var pct = Math.max(0, Math.min(100, (s.total / MAX) * 100));
    var seenPip = {};
    var pips = TIERS.filter(function (tier) {
      // Two gifts can sit on the same rung, for example the mat and the putty at
      // £45. One rung, one pip.
      if (seenPip[tier.threshold]) return false;
      seenPip[tier.threshold] = true;
      return true;
    }).map(function (tier) {
      var won = s.total >= tier.threshold;
      return (
        '<span class="ff-gl__pip" style="left:' +
        (tier.threshold / MAX) * 100 +
        '%" data-won="' +
        won +
        '" aria-hidden="true"></span>'
      );
    }).join('');
    return (
      '<div class="ff-gl__track"><span class="ff-gl__fill" style="width:' + pct + '%"></span>' + pips + '</div>'
    );
  }

  function statusLine(s) {
    if (s.next) {
      if (s.unlockedCount === 0) {
        return 'Spend ' + money(s.next.remaining) + ' more to unlock your first free gift';
      }
      return money(s.next.remaining) + ' more for the ' + s.next.tier.name;
    }
    return 'All ' + TIERS.length + ' free gifts unlocked';
  }

  function tiersHtml(s) {
    return s.rows
      .map(function (r) {
        var st = r.inCart ? 'won' : r.declined ? 'declined' : r.unlocked ? 'declined' : 'locked';
        var meta;
        var action = '';

        if (r.inCart) {
          meta = 'Added free, worth ' + money(r.tier.worth);
          action =
            '<button type="button" class="ff-gl__action" data-ff-gift-remove="' +
            esc(r.tier.key) +
            '">Remove</button>';
        } else if (r.unlocked) {
          meta = 'Yours at ' + money(r.tier.threshold);
          action =
            '<button type="button" class="ff-gl__action" data-variant="claim" data-ff-gift-claim="' +
            esc(r.tier.key) +
            '">Add it back</button>';
        } else {
          meta = 'Free at ' + money(r.tier.threshold) + ', ' + money(r.remaining) + ' to go';
        }

        var thumb = (r.image || r.tier.image)
          ? '<img class="ff-gl__thumb" src="' +
            esc((r.image || r.tier.image)) +
            '" width="40" height="40" alt="" loading="lazy">'
          : '<span class="ff-gl__thumb"></span>';

        return (
          '<li class="ff-gl__tier" data-state="' +
          st +
          '">' +
          thumb +
          '<span class="ff-gl__copy"><span class="ff-gl__name">' +
          esc(r.tier.name) +
          '</span><span class="ff-gl__meta">' +
          esc(meta) +
          '</span>' +
          pickerHtml(r) +
          '</span>' +
          action +
          '</li>'
        );
      })
      .join('');
  }

  // Resistance picker for a rung that offers one gift in several variants. Only
  // shown once the rung is unlocked, because choosing a colour you have not
  // earned yet is noise. The chosen chip is the source of truth for reconcile.
  function pickerHtml(r) {
    var tier = r.tier;
    if (!tier.variants || tier.variants.length < 2) return '';
    if (!r.unlocked) {
      return '<span class="ff-gl__pickhint">' + esc(tier.chooseLabel || 'Choose your resistance when you unlock it') + '</span>';
    }
    var chosen = chosenVariantId(tier);
    var chips = tier.variants
      .map(function (v) {
        var on = v.variantId === chosen;
        return (
          '<button type="button" class="ff-gl__chip" data-ff-gift-choose="' +
          esc(tier.key) +
          '" data-ff-gift-variant="' +
          v.variantId +
          '" data-on="' +
          on +
          '" aria-pressed="' +
          on +
          '"' +
          (v.swatch ? ' style="--ff-swatch:' + esc(v.swatch) + '"' : '') +
          '><span class="ff-gl__chipdot" aria-hidden="true"></span>' +
          esc(v.label) +
          '</button>'
        );
      })
      .join('');
    return (
      '<span class="ff-gl__picker"><span class="ff-gl__pickhint">' +
      esc(tier.chooseLabel || 'Choose your resistance') +
      '</span><span class="ff-gl__chips">' +
      chips +
      '</span></span>'
    );
  }

  function ladderHtml(s, variant) {
    if (variant === 'drawer') return drawerHtml(s);

    return (
      '<div class="ff-gl__head"><p class="ff-gl__title">Free gifts</p>' +
      '<p class="ff-gl__status" role="status" aria-live="polite">' +
      esc(statusLine(s)) +
      '</p></div>' +
      (variant === 'compact' ? '' : trackHtml(s)) +
      '<ul class="ff-gl__tiers">' +
      tiersHtml(s) +
      '</ul>'
    );
  }

  function flag(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function setFlag(key, value) {
    try {
      window.localStorage.setItem(key, value ? '1' : '0');
    } catch (e) {}
  }

  // The rung worth putting in front of the shopper. A gift they qualify for but
  // took out beats a locked one, because getting it back is a single tap and it
  // is already paid for.
  function heroRow(s) {
    var reclaimable = s.rows.filter(function (r) {
      return r.declined;
    })[0];
    return reclaimable || s.next || null;
  }

  // Compact form for the cart drawer, where vertical space belongs to the
  // shopper's own products. One rung, the gifts already won as chips, and the
  // full ladder a tap away.
  function drawerHtml(s) {
    var hero = heroRow(s);
    var won = s.rows.filter(function (r) {
      return r.inCart;
    });
    var open = flag(LS_DRAWER_OPEN);

    var heroBlock;
    if (!hero) {
      heroBlock =
        '<div class="ff-gl__hero" data-state="complete">' +
        '<span class="ff-gl__hero-copy"><span class="ff-gl__hero-name">' +
        'All ' + TIERS.length + ' free gifts unlocked</span>' +
        '<span class="ff-gl__hero-meta">Worth ' +
        esc(money(TIERS.reduce(function (sum, t) { return sum + t.worth; }, 0))) +
        ' in total</span></span></div>';
    } else {
      var thumb = hero.tier.image
        ? '<img class="ff-gl__thumb" src="' +
          esc(hero.tier.image) +
          '" width="40" height="40" alt="" loading="lazy">'
        : '<span class="ff-gl__thumb"></span>';
      var meta = hero.declined
        ? 'Unlocked, worth ' + money(hero.tier.worth)
        : money(hero.remaining) + ' away · worth ' + money(hero.tier.worth);
      var action = hero.declined
        ? '<button type="button" class="ff-gl__action" data-variant="claim" data-ff-gift-claim="' +
          esc(hero.tier.key) +
          '">Add it back</button>'
        : '';
      heroBlock =
        '<div class="ff-gl__hero" data-state="' +
        (hero.declined ? 'declined' : 'locked') +
        '">' +
        thumb +
        '<span class="ff-gl__hero-copy"><span class="ff-gl__hero-name">' +
        esc(hero.tier.name) +
        '</span><span class="ff-gl__hero-meta">' +
        esc(meta) +
        '</span></span>' +
        action +
        '</div>';
    }

    var chips = won
      .map(function (r) {
        return (
          '<span class="ff-gl__chip"><svg viewBox="0 0 10 10" fill="none" aria-hidden="true">' +
          '<path d="M1.5 5.2 4 7.5 8.5 2.5" stroke="currentColor" stroke-width="1.6" ' +
          'stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          esc(r.tier.name) +
          '</span>'
        );
      })
      .join('');

    var toggle =
      '<button type="button" class="ff-gl__more" data-ff-gift-more aria-expanded="' +
      String(open) +
      '">' +
      (open ? 'Hide gifts' : 'All ' + TIERS.length + ' gifts') +
      '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 4.5 6 8l4-3.5" ' +
      'stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';

    return (
      '<p class="ff-gl__status ff-gl__status--sr" role="status" aria-live="polite">' +
      esc(statusLine(s)) +
      '</p>' +
      trackHtml(s) +
      heroBlock +
      '<div class="ff-gl__chips">' + chips + toggle + '</div>' +
      '<ul class="ff-gl__tiers" data-ff-gift-panel' +
      (open ? '' : ' hidden') +
      '>' +
      tiersHtml(s) +
      '</ul>'
    );
  }

  function readJson(el, attr, fallback) {
    var raw = el.getAttribute(attr);
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return fallback;
    }
  }

  // The price of whatever the shopper has actually selected, so the prompt is
  // right on a product whose variants are not all the same price.
  function selectedPrice(mount) {
    var prices = readJson(mount, 'data-ff-prices', null);
    var formId = mount.getAttribute('data-ff-form');
    if (prices) {
      var scope = formId ? document.getElementById(formId) : null;
      var field = scope
        ? scope.querySelector('[name="id"]')
        : document.querySelector('form[action*="/cart/add"] [name="id"]');
      var id = field && field.value;
      if (id && prices[id] != null) return prices[id];
    }
    return parseInt(mount.getAttribute('data-ff-price'), 10) || 0;
  }

  // Product-page prompt: what this item does to the ladder, and the cheapest
  // thing on the site that would close what is left.
  function ctaHtml(s, mount) {
    var price = selectedPrice(mount);
    if (price <= 0) return '';

    var after = s.total + price;
    var newly = TIERS.filter(function (t) {
      return t.threshold > s.total && t.threshold <= after;
    });

    if (newly.length) {
      var names = newly
        .map(function (t) {
          return t.name;
        })
        .join(' and the ');
      return (
        '<p class="ff-cta__line" data-state="unlocks">' +
        '<span class="ff-cta__badge">Free gift</span>' +
        '<span>Add this and the ' +
        esc(names) +
        (newly.length > 1 ? ' are yours free' : ' is yours free') +
        '</span></p>'
      );
    }

    var pending = TIERS.filter(function (t) {
      return t.threshold > after;
    })[0];
    if (!pending) return '';

    var gap = pending.threshold - after;
    var line =
      '<p class="ff-cta__line" data-state="near">' +
      '<span class="ff-cta__badge">Free gift</span>' +
      '<span>With this in your basket you would be <strong>' +
      esc(money(gap)) +
      '</strong> from a free ' +
      esc(pending.name) +
      '</span></p>';

    // Cheapest in-stock product that closes the gap on its own. Anything that
    // does not close it would leave the shopper short and is worse than silence.
    var here = mount.getAttribute('data-ff-handle');
    var candidates = readJson(mount, 'data-ff-candidates', []) || [];
    var pick = null;
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c || !c.price || c.handle === here) continue;
      if (c.price >= gap && (!pick || c.price < pick.price)) pick = c;
    }
    if (pick) {
      line +=
        '<a class="ff-cta__suggest" href="' +
        esc(pick.url) +
        '">Add ' +
        esc(pick.title) +
        ' (' +
        esc(money(pick.price)) +
        ') to unlock it</a>';
    }
    return line;
  }

  // The bar is a progress chart, not a call to action. A "View basket" link was
  // doing nothing useful next to the message and made it read as an advert; what
  // a shopper actually wants mid-scroll is where they are and what is next.
  function barHtml(s) {
    var msg;
    if (s.next && s.wonCount === 0) {
      msg =
        '<strong>' + esc(money(s.next.remaining)) + '</strong>' +
        '<span> to your first free gift</span>';
    } else if (s.next) {
      msg =
        '<strong>' + s.wonCount + (s.wonCount === 1 ? ' gift in' : ' gifts in') + '</strong>' +
        '<span> ' + esc(money(s.next.remaining) + ' to the ' + s.next.tier.name) + '</span>';
    } else {
      msg = '<strong>All ' + TIERS.length + ' gifts unlocked</strong><span> worth ' +
        esc(money(TIERS.reduce(function (n, t) { return n + t.worth; }, 0))) + '</span>';
    }

    var pct = Math.max(0, Math.min(100, (s.total / MAX) * 100));
    var nodes = TIERS.map(function (tier) {
      var won = s.total >= tier.threshold;
      var left = (tier.threshold / MAX) * 100;
      return (
        '<span class="ff-gl-bar__node" style="left:' + left + '%" data-won="' + won + '" aria-hidden="true"></span>' +
        '<span class="ff-gl-bar__tick" style="left:' + left + '%" data-won="' + won + '" aria-hidden="true">' +
        esc(money(tier.threshold).replace('.00', '')) +
        '</span>'
      );
    }).join('');

    // Only ever a button when there is a real action: a gift they qualify for and
    // took out. Otherwise the bar carries no CTA at all.
    var claim = s.rows.filter(function (r) { return r.declined; })[0];
    var cta = claim
      ? '<button type="button" class="ff-gl-bar__cta" data-ff-gift-claim="' +
        esc(claim.tier.key) + '">Add it back</button>'
      : '';

    // The message becomes the link to the explainer page, and the buttons stay as
    // siblings. Deliberately not a link wrapped round the whole panel: an <a> cannot
    // legally contain the collapse button, and an absolutely positioned overlay link
    // would swallow taps meant for it.
    var chevronRight =
      '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true" class="ff-gl-bar__arrow">' +
      '<path d="M4.5 2 8 6l-3.5 4" stroke="currentColor" stroke-width="1.75" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var body =
      '<p class="ff-gl-bar__msg" role="status" aria-live="polite">' + msg + '</p>';

    var main = PAGE_URL
      ? '<a class="ff-gl-bar__main" href="' + esc(PAGE_URL) + '">' +
        body +
        '<span class="ff-gl-bar__more">See the gifts' + chevronRight + '</span>' +
        '</a>'
      : '<div class="ff-gl-bar__main">' + body + '</div>';

    return (
      '<div class="ff-gl-bar__inner">' +
      main +
      '<div class="ff-gl-bar__aside">' +
      cta +
      '<button type="button" class="ff-gl-bar__toggle" data-ff-gift-toggle aria-label="Hide free gift progress">' +
      '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 4.5 6 8l4-3.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '</div>' +
      '<div class="ff-gl-bar__meter">' +
      '<span class="ff-gl-bar__meterfill" style="width:' + pct + '%"></span>' +
      nodes +
      '</div>' +
      '</div>'
    );
  }

  function render() {
    var s = state();

    document.querySelectorAll('[data-ff-gift-ladder]').forEach(function (mount) {
      var variant = mount.getAttribute('data-variant') || 'full';
      mount.innerHTML = ladderHtml(s, variant);
      mount.hidden = false;
      mount.classList.add('ff-gl');
      mount.setAttribute('data-variant', variant);
    });

    document.querySelectorAll('[data-ff-gift-cta]').forEach(function (mount) {
      var html = ctaHtml(s, mount);
      mount.innerHTML = html;
      mount.hidden = !html;
      mount.classList.add('ff-cta');
    });

    var collapsed = flag(LS_COLLAPSED);

    document.querySelectorAll('[data-ff-gift-bar]').forEach(function (mount) {
      // The cart page already shows the full ladder, so the floating bar would
      // just be shouting the same thing twice.
      if (document.body.classList.contains('template-cart')) {
        mount.hidden = true;
        return;
      }
      mount.innerHTML = barHtml(s);
      mount.hidden = false;
      mount.classList.add('ff-gl-bar');
      mount.setAttribute('data-collapsed', String(collapsed));
    });

    positionBar();
  }

  // Keep the floating bar clear of everything else the theme pins to the bottom
  // of the screen on mobile: the dock (Home / Menu / Search / Shop / Cart /
  // Account) and, on collection pages, the "Filter and sort" button.
  //
  // Two traps here, both of which cost a live bug:
  //   - The dock is `nav.mobile-dock`. Matching `.mobile-dock-section`, the
  //     wrapper, measures a box with no height, so the offset came out as 0 and
  //     the bar sat directly on top of the navigation.
  //   - getBoundingClientRect is the wrong measure, because the dock is
  //     translated off-screen as you scroll and would then measure as clear.
  //     offsetHeight is layout height and ignores the transform, so the bar keeps
  //     its place instead of jumping over the nav each time it hides.
  var BOTTOM_CHROME = 'nav.mobile-dock, .mobile-dock, facet-sticky';

  function isVisible(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    return el.offsetHeight > 0;
  }

  function positionBar() {
    var offset = 0;
    var seen = [];
    document.querySelectorAll(BOTTOM_CHROME).forEach(function (el) {
      if (el.closest('[data-ff-gift-bar]')) return;
      if (seen.indexOf(el) !== -1) return;      // .mobile-dock can match twice
      seen.push(el);
      if (isVisible(el)) offset += el.offsetHeight;
    });
    document.documentElement.style.setProperty('--ff-gl-bar-offset', offset + 'px');
  }

  // The dock and the filter button come and go with scrolling and with the
  // template, so re-measure rather than trusting the value from first paint.
  var positionQueued = false;
  function queuePosition() {
    if (positionQueued) return;
    positionQueued = true;
    window.requestAnimationFrame(function () {
      positionQueued = false;
      positionBar();
    });
  }

  /* ----------------------------------------------------------------- events */

  document.addEventListener('click', function (event) {
    var claim = event.target.closest('[data-ff-gift-claim]');
    if (claim) {
      event.preventDefault();
      var claimKey = claim.getAttribute('data-ff-gift-claim');
      var declined = readSet(LS_DECLINED);
      var seenNow = readSet(LS_SEEN);
      declined.delete(claimKey);
      // Clearing the decline alone is not enough. The tier is still in `seen` with no
      // line in the cart, which is exactly the fingerprint of a manual removal, so
      // the next reconcile would re-decline it and the button would do nothing.
      seenNow.delete(claimKey);
      writeSet(LS_DECLINED, declined);
      writeSet(LS_SEEN, seenNow);
      claim.disabled = true;
      schedule(0);
      return;
    }

    var remove = event.target.closest('[data-ff-gift-remove]');
    if (remove) {
      event.preventDefault();
      var tier = tierByKey(remove.getAttribute('data-ff-gift-remove'));
      if (!tier) return;
      var declined2 = readSet(LS_DECLINED);
      declined2.add(tier.key);
      writeSet(LS_DECLINED, declined2);
      remove.disabled = true;
      (async function () {
        try {
          var updates = {};
          tierVariantIds(tier).forEach(function (vid) {
            updates[vid] = 0;
          });
          var next = await applyQuantities(updates);
          if (isFullCart(next)) {
            cart = next;
            announce(cart);
          }
        } catch (err) {
          /* reconcile picks it up */
        }
        schedule(0);
      })();
      return;
    }

    // Picking a resistance. The write goes through the same reconcile as
    // everything else, so the swap is one absolute-quantity request.
    var choose = event.target.closest('[data-ff-gift-choose]');
    if (choose) {
      event.preventDefault();
      var chooseTier = tierByKey(choose.getAttribute('data-ff-gift-choose'));
      var pickedId = parseInt(choose.getAttribute('data-ff-gift-variant'), 10);
      if (!chooseTier || !pickedId) return;
      if (tierVariantIds(chooseTier).indexOf(pickedId) === -1) return;
      var choiceMap = readChoice();
      if (choiceMap[chooseTier.key] === pickedId) return;
      choiceMap[chooseTier.key] = pickedId;
      writeChoice(choiceMap);
      // A rung the shopper had removed is being asked for again by picking a
      // resistance, so clear the decline as well.
      var dec = readSet(LS_DECLINED);
      if (dec.has(chooseTier.key)) {
        dec.delete(chooseTier.key);
        writeSet(LS_DECLINED, dec);
      }
      render();
      schedule(0);
      return;
    }

    var toggle = event.target.closest('[data-ff-gift-toggle]');
    if (toggle) {
      event.preventDefault();
      var bar = toggle.closest('[data-ff-gift-bar]');
      var next2 = bar.getAttribute('data-collapsed') !== 'true';
      bar.setAttribute('data-collapsed', String(next2));
      setFlag(LS_COLLAPSED, next2);
      return;
    }

    // Expand or fold the full ladder inside the drawer. Re-rendering rather than
    // toggling in place keeps the button label and aria-expanded honest.
    var more = event.target.closest('[data-ff-gift-more]');
    if (more) {
      event.preventDefault();
      setFlag(LS_DRAWER_OPEN, !flag(LS_DRAWER_OPEN));
      render();
    }
  });

  // A variant switch changes the price, so the product-page prompt has to be
  // recomputed. The theme dispatches variant:change on the product form with
  // bubbles unset, so this listens in the capture phase, which still runs for a
  // non-bubbling event. The variant in the payload carries the price, which
  // beats reading it back out of the form.
  document.addEventListener(
    'variant:change',
    function (event) {
      var mounts = document.querySelectorAll('[data-ff-gift-cta]');
      if (!mounts.length) return;
      var variant = event.detail && event.detail.variant;
      mounts.forEach(function (mount) {
        if (variant && typeof variant.price === 'number') {
          mount.setAttribute('data-ff-price', String(variant.price));
        } else if (variant === null) {
          mount.setAttribute('data-ff-price', '0'); // sold out or unavailable
        }
      });
      render();
    },
    true
  );

  // Fallback for any picker that writes the hidden id field without announcing it.
  document.addEventListener('change', function (event) {
    if (!document.querySelector('[data-ff-gift-cta]')) return;
    if (event.target.closest('form[action*="/cart/add"]')) render();
  });

  // The theme fires this after it has morphed the drawer and cart page, so our
  // mounts are fresh by the time we run.
  document.addEventListener('cart:updated', function (event) {
    var incoming = event.detail && event.detail.cart;
    if (incoming && incoming.items) cart = incoming;
    schedule();
  });

  // Catch cart writes we did not make: quick-add buttons, other scripts, apps.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var ours = false;
    try {
      var h = (init && init.headers) || {};
      ours = typeof h.get === 'function' ? !!h.get('X-FF-Gift') : !!h['X-FF-Gift'];
    } catch (e) {}

    var result = nativeFetch.apply(this, arguments);

    if (!ours && /\/cart\/(add|change|update|clear)/.test(String(url))) {
      result
        .then(function (res) {
          // change.js and update.js hand back the whole cart, so reuse it rather
          // than spending another request. add.js only returns the lines it added,
          // which is not a cart, so that path has to re-read.
          return res
            .clone()
            .json()
            .then(function (body) {
              cart = isFullCart(body) ? body : null;
            })
            .catch(function () {
              cart = null;
            });
        })
        .then(function () {
          schedule(200);
        })
        .catch(function () {
          cart = null;
          schedule(250);
        });
    }
    return result;
  };

  // Section morphs can replace a mount with an empty one without firing an event.
  var observer = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (
          node.matches('[data-ff-gift-ladder], [data-ff-gift-bar], [data-ff-gift-cta]') ||
          node.querySelector('[data-ff-gift-ladder], [data-ff-gift-bar], [data-ff-gift-cta]')
        ) {
          render();
          return;
        }
      }
    }
  });

  function boot() {
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', queuePosition, { passive: true });
    window.addEventListener('scroll', queuePosition, { passive: true });
    window.addEventListener('orientationchange', queuePosition, { passive: true });
    render();
    schedule(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
